import { useEffect, useState } from "react";
import { Loader2, Linkedin, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";

const GROUP_ORDER = ["Leadership", "Operations & Support", "Zonal Managers"];
const GROUP_BLURB = {
  "Leadership": "The vision and direction behind Yayra Glover Organic Inputs.",
  "Operations & Support": "The people keeping orders, stock and farmer support running smoothly.",
  "Zonal Managers": "Our people on the ground, serving farmers across Ghana's regions.",
};

const initials = (name) =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

function Avatar({ member, large }) {
  const size = large ? "h-44" : "h-40";
  if (member.photo) {
    return <img src={member.photo} alt={member.name} className={`w-full ${size} object-cover`} />;
  }
  return (
    <div className={`w-full ${size} flex items-center justify-center bg-gradient-to-br from-brand to-brand-hover`}>
      <span className="font-display text-4xl text-brand-ochre">{initials(member.name)}</span>
    </div>
  );
}

function MemberCard({ member, large }) {
  return (
    <div
      data-testid={`team-member-${member.team_id}`}
      className="group bg-white border border-brand/10 rounded-sm overflow-hidden hover:shadow-lg transition-shadow flex flex-col"
    >
      <div className="overflow-hidden bg-brand-surface">
        <Avatar member={member} large={large} />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className={`font-display ${large ? "text-xl" : "text-lg"} text-brand leading-tight`}>{member.name}</h3>
        <p className="mt-1 text-sm text-brand-terracotta font-medium">{member.title}</p>
        {member.bio && <p className="mt-3 text-sm text-brand-muted leading-relaxed flex-1">{member.bio}</p>}
        {(member.linkedin || member.email) && (
          <div className="mt-4 flex items-center gap-2">
            {member.linkedin && (
              <a href={member.linkedin} target="_blank" rel="noreferrer noopener" aria-label={`${member.name} on LinkedIn`}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-brand/15 text-brand hover:bg-brand hover:text-white transition-colors">
                <Linkedin className="w-4 h-4" />
              </a>
            )}
            {member.email && (
              <a href={`mailto:${member.email}`} aria-label={`Email ${member.name}`}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-brand/15 text-brand hover:bg-brand hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Team() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/team").then((r) => setMembers(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const groups = GROUP_ORDER
    .map((g) => ({ name: g, people: members.filter((m) => m.group === g).sort((a, b) => a.order - b.order) }))
    .filter((g) => g.people.length > 0);

  // any members whose group isn't in GROUP_ORDER
  const others = members.filter((m) => !GROUP_ORDER.includes(m.group));

  return (
    <div data-testid="team-page">
      <Seo
        title="Our Team"
        path="/team"
        description="Meet the team behind Yayra Glover Organic Inputs — the leadership, operations and regional managers strengthening Ghana's organic commodity value chain."
      />
      <section className="bg-brand text-brand-bg noise-overlay">
        <div className="mx-auto max-w-4xl px-6 py-20 text-center">
          <span className="text-xs uppercase tracking-[0.28em] text-brand-ochre font-semibold">The People</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl">Meet Our Team</h1>
          <p className="mt-4 text-brand-bg/80 max-w-xl mx-auto">
            A dedicated team of professionals working across Ghana to bring certified‑organic
            farming solutions to every farmer.
          </p>
        </div>
      </section>

      {loading ? (
        <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>
      ) : (
        <div className="mx-auto max-w-6xl px-6 py-16 space-y-16">
          {groups.map((group, gi) => (
            <section key={group.name} data-testid={`team-group-${group.name}`}>
              <div className="text-center max-w-2xl mx-auto">
                <h2 className="font-display text-2xl sm:text-3xl text-brand">{group.name}</h2>
                {GROUP_BLURB[group.name] && <p className="mt-2 text-brand-muted text-sm">{GROUP_BLURB[group.name]}</p>}
              </div>
              <div
                className={`mt-8 grid gap-6 ${
                  gi === 0
                    ? "sm:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto"
                    : "sm:grid-cols-2 lg:grid-cols-4"
                }`}
              >
                {group.people.map((m) => <MemberCard key={m.team_id} member={m} large={gi === 0} />)}
              </div>
            </section>
          ))}

          {others.length > 0 && (
            <section>
              <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {others.map((m) => <MemberCard key={m.team_id} member={m} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
